import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/AuthContext';
import styles from './Sidebar.module.css';

type NavItem = {
  href: string;
  label: string;
  icon: string;
  roles: string[];
  module?: string;
  group?: string;
};

const groupConfig: Record<string, { label: string; icon: string }> = {
  alumnos: { label: 'Alumnos', icon: '👨‍🎓' },
  personal: { label: 'Personal', icon: '👔' },
  comunicacion: { label: 'Comunicación', icon: '💬' },
  finanzas: { label: 'Finanzas', icon: '💰' },
  herramientas: { label: 'Herramientas', icon: '🔧' },
};

const groupOrder = ['alumnos', 'personal', 'comunicacion', 'finanzas', 'herramientas'];

const navItems: NavItem[] = [
  { href: '/dashboard', label: 'Inicio', icon: '🏠', roles: ['*'] },
  { href: '/dashboard/profile', label: 'Mi Perfil', icon: '👤', roles: ['*'] },
  { href: '/dashboard/settings', label: 'Configurar', icon: '⚙️', roles: ['SCHOOL_ADMIN'] },

  // Alumnos
  {
    href: '/dashboard/students',
    label: 'Alta alumnos',
    icon: '➕',
    roles: ['SCHOOL_ADMIN', 'STAFF'],
    group: 'alumnos',
  },
  {
    href: '/dashboard/enrollments',
    label: 'Inscripciones',
    icon: '📝',
    roles: ['SCHOOL_ADMIN', 'STAFF'],
    group: 'alumnos',
  },
  {
    href: '/dashboard/attendance',
    label: 'Asistencia',
    icon: '📋',
    roles: ['SCHOOL_ADMIN', 'TEACHER'],
    module: 'attendance',
    group: 'alumnos',
  },
  {
    href: '/dashboard/security',
    label: 'Seguridad',
    icon: '🔒',
    roles: ['SCHOOL_ADMIN', 'STAFF'],
    module: 'security',
    group: 'alumnos',
  },
  {
    href: '/dashboard/grades',
    label: 'Calificaciones',
    icon: '📊',
    roles: ['SCHOOL_ADMIN', 'TEACHER'],
    module: 'grades',
    group: 'alumnos',
  },

  // Personal
  {
    href: '/dashboard/staff',
    label: 'Alta de personal',
    icon: '➕',
    roles: ['SCHOOL_ADMIN'],
    group: 'personal',
  },

  // Comunicación
  {
    href: '/dashboard/meals',
    label: 'Alimentos',
    icon: '🍽️',
    roles: ['SCHOOL_ADMIN', 'STAFF', 'TEACHER', 'PARENT'],
    module: 'meals',
    group: 'comunicacion',
  },
  {
    href: '/dashboard/announcements',
    label: 'Anuncios',
    icon: '📢',
    roles: ['*'],
    module: 'announcements',
    group: 'comunicacion',
  },
  {
    href: '/dashboard/chat',
    label: 'Chat',
    icon: '💬',
    roles: ['*'],
    module: 'chat',
    group: 'comunicacion',
  },

  // Finanzas
  {
    href: '/dashboard/finance/tablero',
    label: 'Tablero',
    icon: '📊',
    roles: ['SCHOOL_ADMIN'],
    module: 'finance',
    group: 'finanzas',
  },
  {
    href: '/dashboard/finance/pagos',
    label: 'Pagos',
    icon: '💰',
    roles: ['SCHOOL_ADMIN'],
    module: 'finance',
    group: 'finanzas',
  },
  {
    href: '/dashboard/expenses',
    label: 'Egresos',
    icon: '💳',
    roles: ['SCHOOL_ADMIN', 'STAFF'],
    group: 'finanzas',
  },

  // Herramientas
  {
    href: '/dashboard/polls',
    label: 'Encuestas',
    icon: '📊',
    roles: ['*'],
    module: 'polls',
    group: 'herramientas',
  },

  // Rol-specific
  { href: '/dashboard/parent', label: 'Mis Hijos', icon: '👶', roles: ['PARENT'] },
  { href: '/dashboard/teacher', label: 'Mis Grupos', icon: '📖', roles: ['TEACHER'] },
  {
    href: '/dashboard/calendar',
    label: 'Calendario',
    icon: '📅',
    roles: ['*'],
    module: 'calendar',
  },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (!user) return null;

  const role = user.role;
  const schoolModules = user.schoolModules || {};
  const hasModules = (m?: Record<string, boolean>) => m && Object.keys(m).length > 0;

  const isVisible = (item: NavItem) => {
    if (!(item.roles.includes('*') || item.roles.includes(role))) return false;
    if (item.module && hasModules(schoolModules)) {
      return !!schoolModules[item.module];
    }
    return true;
  };

  const grouped = navItems.filter((i) => i.group && isVisible(i));
  const standalone = navItems.filter((i) => !i.group && isVisible(i));

  const groupItems: Record<string, NavItem[]> = {};
  for (const g of groupOrder) {
    groupItems[g] = grouped.filter((i) => i.group === g);
  }

  const toggleGroup = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userName = user.name || user.email;
  const schoolName = user.schoolName || 'Control Escolar';

  return (
    <>
      <button className={styles.hamburger} onClick={() => setOpen(!open)} aria-label="Menú">
        <span className={`${styles.bar} ${open ? styles.open : ''}`} />
        <span className={`${styles.bar} ${open ? styles.open : ''}`} />
        <span className={`${styles.bar} ${open ? styles.open : ''}`} />
      </button>

      {open && <div className={styles.overlay} onClick={() => setOpen(false)} />}

      <aside className={`${styles.sidebar} ${open ? styles.show : ''}`}>
        <div className={styles.logo}>
          <h2>{schoolName}</h2>
        </div>

        <div className={styles.userBadge}>
          <span className={styles.avatar}>{userName.charAt(0).toUpperCase()}</span>
          <div>
            <p className={styles.userName}>{userName}</p>
            <p className={styles.userRole}>
              {role === 'SCHOOL_ADMIN'
                ? 'Administrador'
                : role === 'TEACHER'
                  ? 'Docente'
                  : role === 'PARENT'
                    ? 'Padre de Familia'
                    : role === 'SUPER_ADMIN'
                      ? 'Super Admin'
                      : role}
            </p>
          </div>
        </div>

        <nav className={styles.nav}>
          {standalone.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
                onClick={() => setOpen(false)}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}

          {groupOrder.map((gKey) => {
            const items = groupItems[gKey];
            if (items.length === 0) return null;
            const cfg = groupConfig[gKey];
            const isCollapsed = collapsed[gKey];

            return (
              <div key={gKey} className={styles.group}>
                <button
                  className={styles.groupHeader}
                  onClick={() => toggleGroup(gKey)}
                  aria-expanded={!isCollapsed}
                >
                  <span>{cfg?.icon}</span>
                  <span>{cfg?.label}</span>
                  <span className={`${styles.chevron} ${isCollapsed ? '' : styles.chevronOpen}`}>
                    ▾
                  </span>
                </button>
                {!isCollapsed && (
                  <div className={styles.subItems}>
                    {items.map((item) => {
                      const active = isActive(item.href);
                      return (
                        <Link
                          key={item.href}
                          to={item.href}
                          className={`${styles.subItem} ${active ? styles.subItemActive : ''}`}
                          onClick={() => setOpen(false)}
                        >
                          <span>{item.icon}</span>
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={styles.spacer} />

        <div className={styles.logoutForm}>
          <button onClick={handleLogout} className={styles.logoutBtn}>
            Cerrar Sesión
          </button>
        </div>
      </aside>
    </>
  );
}
